import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { AccountType, Currency } from '@seapoint/server/enums';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '../../../../design-system/form/form-primitives';
import { Input } from '../../../../design-system/input/input';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { isInvestmentAccount } from '../../../../utils/accounts/is-investment-account';
import { isModulrAccount } from '../../../../utils/accounts/is-modulr-account';
import { cn } from '../../../../utils/cn';
import { getBankAccountName } from '../../../../utils/get-bank-account-name';
import { getCurrencySymbol } from '../../../../utils/get-currency-symbol';
import { getMinPaymentAmountErrorMessage } from '../../../../utils/get-min-payment-amount-error-message';
import { AccountSelectItem } from '../../components/account-select-item';
import { getEligibleSourceAccounts } from '../utils/get-eligible-source-accounts';
import { CurrencyAccountsNotFound } from './currency-accounts-not-found';

// See min and max payment amount in Modulr API docs
// https://modulr.readme.io/reference/createpayments
const MINIMUM_PAYMENT_AMOUNT = 0.01;
const MAXIMUM_PAYMENT_AMOUNT = 2147483647;

const createFormSchema = (currency?: Currency, balance?: number) =>
  z.object({
    amount: z.coerce
      .number({ message: 'Payment amount is required' })
      .min(MINIMUM_PAYMENT_AMOUNT, { message: getMinPaymentAmountErrorMessage(currency) })
      .max(balance ?? MAXIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must not exceed the available balance' })
      .max(MAXIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must not exceed 2,147,483,647' }),
    sourceAccountId: z.string(),
    destinationAccountId: z
      .string({ message: 'Destination account is required' })
      .nonempty({ message: 'Destination account is required' }),
  });

export type InternalPaymentFormValues = z.infer<ReturnType<typeof createFormSchema>>;

type OnSubmitArgs = {
  values: InternalPaymentFormValues;
  currency: Currency;
  sourceAccountName: string;
  sourceAccountLogo?: string;
  destinationAccountType: AccountType;
  destinationAccountName: string;
  destinationAccountLogo?: string;
  isInstantTransfer?: boolean;
};

type InternalPaymentFormProps = {
  accounts: AccountView[];
  isLoading: boolean;
  initialValues?: Partial<Omit<InternalPaymentFormValues, 'amount'>>;
  onSubmit: ({
    values,
    currency,
    sourceAccountName,
    sourceAccountLogo,
    destinationAccountType,
    destinationAccountName,
    destinationAccountLogo,
    isInstantTransfer,
  }: OnSubmitArgs) => void;
};

export const InternalPaymentForm = ({ accounts, initialValues, onSubmit, isLoading }: InternalPaymentFormProps) => {
  const eligibleSourceAccounts = getEligibleSourceAccounts({
    accounts,
    destinationAccountId: initialValues?.destinationAccountId,
  });
  const initialSourceAccount =
    eligibleSourceAccounts.find((a) => a.id === initialValues?.sourceAccountId) ?? eligibleSourceAccounts[0];
  const initialDestinationAccount = accounts.find((a) => a.id === initialValues?.destinationAccountId) ?? null;

  const defaultSourceAccountId = initialSourceAccount.id;
  const defaultDestinationAccountId = initialDestinationAccount?.id ?? '';
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState<string>(defaultSourceAccountId);

  const sourceAccount = eligibleSourceAccounts.find((a) => a.id === selectedSourceAccountId);

  const form = useForm<InternalPaymentFormValues>({
    resolver: zodResolver(createFormSchema(sourceAccount?.currency, sourceAccount?.availableBalance ?? 0)),
    defaultValues: {
      sourceAccountId: defaultSourceAccountId,
      destinationAccountId: defaultDestinationAccountId,
    },
    mode: 'onTouched',
  });

  const { errors, isValid } = form.formState;

  const availableDestinationAccounts = accounts
    // We don't show the account that is selected as the source account
    .filter((a) => a.id !== selectedSourceAccountId)
    // We only show accounts that have the same currency as the source account
    .filter((a) => a.currency === sourceAccount?.currency)
    .filter((a) => {
      if (!sourceAccount) {
        return true;
      }

      const sourceIsInvestment = isInvestmentAccount(sourceAccount);

      if (sourceIsInvestment && !isModulrAccount(a)) {
        return false;
      }

      const destIsInvestment = isInvestmentAccount(a);

      if (destIsInvestment && !isModulrAccount(sourceAccount)) {
        return false;
      }

      return true;
    });

  const checkIsInstantTransfer = (sourceAccount: AccountView, destinationAccount: AccountView) => {
    return sourceAccount.institution?.name === 'Seapoint' && destinationAccount.institution?.name === 'Seapoint';
  };

  useEffect(() => {
    // Triggers validation on amount field when source account is changed
    // Not running in on undefined prevents running it on initial render
    if (form.getValues('amount') !== undefined && selectedSourceAccountId) {
      form.trigger('amount');
    }
  }, [form, selectedSourceAccountId]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((formData) => {
          const destinationAccount = availableDestinationAccounts.find((a) => a.id === formData.destinationAccountId);

          if (!sourceAccount || !destinationAccount) {
            return;
          }

          onSubmit({
            values: formData,
            currency: sourceAccount.currency,
            sourceAccountName: getBankAccountName({
              displayName: sourceAccount.displayName,
              institutionName: sourceAccount.institution?.name,
            }),
            sourceAccountLogo: sourceAccount.institution?.logo,
            destinationAccountType: destinationAccount.type,
            destinationAccountName: getBankAccountName({
              displayName: destinationAccount.displayName,
              institutionName: destinationAccount.institution?.name,
            }),
            destinationAccountLogo: destinationAccount.institution?.logo,
            isInstantTransfer: checkIsInstantTransfer(sourceAccount, destinationAccount),
          });
        })}
        className="w-full"
      >
        <Stack align="stretch" space={3}>
          <Stack
            className={cn(
              'rounded-lg border border-border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:outline-hidden',
              {
                'border-destructive': !!errors.amount || !!errors.sourceAccountId,
              },
            )}
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => {
                const currencySymbol = getCurrencySymbol(sourceAccount?.currency ?? '');

                return (
                  <FormItem>
                    <VisuallyHidden>
                      <FormLabel>Amount to be transferred</FormLabel>
                    </VisuallyHidden>

                    <FormControl>
                      <Input
                        required
                        leftIcon={
                          <p className="text-base font-medium text-muted-foreground">{sourceAccount?.currency}</p>
                        }
                        type="text"
                        placeholder={`${currencySymbol}0.00`}
                        rightAlign
                        className={cn(
                          '[appearance:textfield] rounded-t-lg rounded-b-none border-0 bg-white py-2 pr-4 pl-9 text-2xl placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                        )}
                        {...field}
                        value={field.value ? `${currencySymbol}${field.value}` : ''}
                        onChange={(e) => {
                          // Remove currency symbol and non-numeric chars, then convert to number
                          const numericValue = e.target.value.replace(currencySymbol, '').replace(/[^\d.]/g, '');

                          field.onChange(numericValue);
                        }}
                        onFocus={(e) => {
                          if (e.target.value === currencySymbol + '0') {
                            e.target.value = currencySymbol;
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="sourceAccountId"
              render={({ field }) => {
                return (
                  <FormItem>
                    <VisuallyHidden>
                      <FormLabel htmlFor="from-account-select">From Account</FormLabel>
                    </VisuallyHidden>

                    <FormControl>
                      <Select
                        id="from-account-select"
                        aria-labelledby="from-account-label"
                        required
                        placeholder={
                          <AccountSelectItem
                            balance={initialSourceAccount.availableBalance ?? 0}
                            currency={initialSourceAccount.currency}
                            institutionLogo={initialSourceAccount.institution?.logo}
                            name={initialSourceAccount.displayName ?? 'Bank Account'}
                            fspAdapter={initialSourceAccount.fspAdapter}
                          />
                        }
                        onValueChange={(value) => {
                          const selectedAccount = eligibleSourceAccounts.find((a) => a.id === value);

                          field.onChange(value);

                          if (selectedAccount) {
                            setSelectedSourceAccountId(selectedAccount.id);
                          }
                        }}
                        defaultValue={selectedSourceAccountId}
                        triggerClassName={cn(
                          'justify-start gap-2 rounded-t-none rounded-b-lg bg-muted py-1.5 focus:ring-0 focus:ring-offset-0 [&_.account-logo]:h-4 [&_.account-logo]:w-4 [&_.account-type]:hidden',
                        )}
                      >
                        {eligibleSourceAccounts.map((account) => {
                          const accountName = getBankAccountName({
                            displayName: account.displayName,
                            institutionName: account.institution?.name,
                          });

                          return (
                            <Select.Item key={account.id} value={account.id} className="py-3">
                              <AccountSelectItem
                                balance={account.availableBalance ?? 0}
                                currency={account.currency}
                                institutionLogo={account.institution?.logo}
                                name={accountName}
                                fspAdapter={account.fspAdapter}
                              />
                            </Select.Item>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          </Stack>

          {availableDestinationAccounts.length > 0 && (
            <>
              <FormField
                control={form.control}
                name="destinationAccountId"
                render={({ field }) => {
                  return (
                    <FormItem onBlur={field.onBlur}>
                      <FormControl>
                        <Select
                          aria-label="Transfer to"
                          required
                          placeholder={
                            initialDestinationAccount ? (
                              <AccountSelectItem
                                balance={initialDestinationAccount.availableBalance ?? 0}
                                currency={initialDestinationAccount.currency}
                                institutionLogo={initialDestinationAccount.institution?.logo}
                                name={initialDestinationAccount.displayName ?? 'Bank Account'}
                                fspAdapter={initialDestinationAccount.fspAdapter}
                              />
                            ) : (
                              'Select account'
                            )
                          }
                          onValueChange={field.onChange}
                          triggerLabel={
                            <FormLabel className="text-xs font-medium text-muted-foreground">Transfer to</FormLabel>
                          }
                          triggerClassName={cn(
                            'rounded-lg border border-border bg-white p-4 text-base font-medium text-black [&_.account-logo]:h-4 [&_.account-logo]:w-4 [&_.account-type]:hidden',
                            { 'border-destructive': !!errors.destinationAccountId },
                          )}
                        >
                          {availableDestinationAccounts.map((account) => {
                            const accountName = getBankAccountName({
                              displayName: account.displayName,
                              institutionName: account.institution?.name,
                            });

                            return (
                              <Select.Item key={account.id} value={account.id} className="py-3">
                                <AccountSelectItem
                                  balance={account.availableBalance ?? 0}
                                  currency={account.currency}
                                  institutionLogo={account.institution?.logo}
                                  name={accountName}
                                  fspAdapter={account.fspAdapter}
                                />
                              </Select.Item>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </FormItem>
                  );
                }}
              />

              <Stack align="stretch" space={1}>
                {errors.amount && <p className="text-sm font-medium text-destructive">{errors.amount.message}</p>}

                {errors.sourceAccountId && (
                  <p className="text-sm font-medium text-destructive">{errors.sourceAccountId.message}</p>
                )}

                {errors.destinationAccountId && (
                  <p className="text-sm font-medium text-destructive">{errors.destinationAccountId.message}</p>
                )}
              </Stack>

              <Button variant="default" size="lg" type="submit" disabled={!isValid} isLoading={isLoading}>
                Continue
              </Button>
            </>
          )}

          {availableDestinationAccounts.length === 0 && sourceAccount?.currency && (
            <CurrencyAccountsNotFound currency={sourceAccount.currency} onOpenAccount={() => {}} />
          )}
        </Stack>
      </form>
    </Form>
  );
};
